/**
 * useVoiceRecording Hook
 *
 * Handles voice recording using the MediaRecorder API.
 * Manages microphone permissions, recording state, and audio capture.
 */

import { useState, useCallback, useRef, useEffect } from 'react';

export interface UseVoiceRecordingOptions {
  /** Maximum recording duration in seconds (default: 120) */
  maxDuration?: number;
  /** Preferred MIME type (default: auto-detect) */
  mimeType?: string;
}

export interface UseVoiceRecordingReturn {
  /** True if browser supports voice recording */
  isSupported: boolean;
  /** True when actively recording */
  isRecording: boolean;
  /** Current recording duration in seconds */
  duration: number;
  /** Real-time audio input level 0–1 (driven by AnalyserNode) */
  audioLevel: number;
  /** Error message if recording failed */
  error: string | null;
  /** Start recording (requests permission if needed) */
  startRecording: () => Promise<void>;
  /** Stop recording and return audio blob */
  stopRecording: () => Promise<Blob>;
  /** Cancel recording without returning blob */
  cancelRecording: () => void;
}

/**
 * Check if voice recording is supported in this browser
 */
function isRecordingSupported(): boolean {
  return (
    typeof MediaRecorder !== 'undefined' &&
    typeof navigator.mediaDevices !== 'undefined' &&
    typeof navigator.mediaDevices.getUserMedia === 'function'
  );
}

/**
 * Get the best supported MIME type for audio recording
 */
function getBestMimeType(): string {
  const types = [
    'audio/webm',
    'audio/webm;codecs=opus',
    'audio/mp4',
    'audio/ogg;codecs=opus',
  ];

  for (const type of types) {
    if (MediaRecorder.isTypeSupported(type)) {
      return type;
    }
  }

  // Fallback
  return 'audio/webm';
}

/**
 * Hook for voice recording with MediaRecorder API
 *
 * @param options - Optional configuration
 * @returns Voice recording interface
 */
export function useVoiceRecording(
  options: UseVoiceRecordingOptions = {}
): UseVoiceRecordingReturn {
  const { maxDuration = 120, mimeType } = options;

  const [isRecording, setIsRecording] = useState(false);
  const [duration, setDuration] = useState(0);
  const [audioLevel, setAudioLevel] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const durationIntervalRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);
  const maxDurationTimeoutRef = useRef<number | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioLevelRAFRef = useRef<number | null>(null);
  // Synchronous re-entrancy guard. `getUserMedia` is async, so two near-
  // simultaneous startRecording() calls (a double-clicked mic button, or a
  // React StrictMode double-invoked mount effect) would each acquire their OWN
  // MediaStream; the first gets overwritten in mediaStreamRef without its
  // tracks stopped — leaking a live mic the browser never releases. Setting
  // this flag synchronously, before the await, lets the second call bail out.
  const isStartingRef = useRef(false);

  const isSupported = isRecordingSupported();

  /**
   * Clean up media stream and recorder
   */
  const cleanup = useCallback(() => {
    // Stop audio level animation
    if (audioLevelRAFRef.current !== null) {
      cancelAnimationFrame(audioLevelRAFRef.current);
      audioLevelRAFRef.current = null;
    }

    // Close AudioContext
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    analyserRef.current = null;

    // Stop all tracks
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }

    // Clear recorder
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current = null;
    }

    // Clear intervals/timeouts
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }

    if (maxDurationTimeoutRef.current) {
      clearTimeout(maxDurationTimeoutRef.current);
      maxDurationTimeoutRef.current = null;
    }

    // Reset state
    setDuration(0);
    setAudioLevel(0);
    setIsRecording(false);
    chunksRef.current = [];
    isStartingRef.current = false;
  }, []);

  /**
   * Start recording
   */
  const startRecording = useCallback(async () => {
    if (!isSupported) {
      const message = 'Voice recording is not supported in this browser';
      setError(message);
      throw new Error(message);
    }

    // Already starting or recording — ignore the duplicate call so we never
    // open a second mic stream that would leak (see isStartingRef above).
    if (isStartingRef.current || mediaRecorderRef.current) {
      return;
    }
    isStartingRef.current = true;

    setError(null);
    chunksRef.current = [];

    try {
      // Request microphone permission
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // The await above can resolve after we've been cleaned up (component
      // unmounted / recording cancelled — isStartingRef reset to false) or
      // after another start already won the race (mediaStreamRef set). In
      // either case, stop THIS stream's tracks immediately and bail, or its
      // mic track leaks with no ref pointing at it to stop later.
      if (!isStartingRef.current || mediaStreamRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      mediaStreamRef.current = stream;

      // Set up real-time audio level analysis
      try {
        const audioContext = new AudioContext();
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        audioContext.createMediaStreamSource(stream).connect(analyser);
        audioContextRef.current = audioContext;
        analyserRef.current = analyser;

        const freqData = new Uint8Array(analyser.frequencyBinCount);
        const updateLevel = () => {
          if (!analyserRef.current) return;
          analyserRef.current.getByteFrequencyData(freqData);
          const avg = freqData.reduce((a, b) => a + b, 0) / freqData.length;
          setAudioLevel(avg / 255);
          audioLevelRAFRef.current = requestAnimationFrame(updateLevel);
        };
        audioLevelRAFRef.current = requestAnimationFrame(updateLevel);
      } catch {
        // AudioContext failure is non-fatal — recording still works, just no level meter
      }

      // Determine MIME type
      const recordMimeType = mimeType || getBestMimeType();

      // Create MediaRecorder
      const recorder = new MediaRecorder(stream, { mimeType: recordMimeType });
      mediaRecorderRef.current = recorder;

      // Collect audio chunks
      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      // Handle errors
      recorder.onerror = (event: Event) => {
        const errorEvent = event as ErrorEvent;
        const message = errorEvent.error?.message || 'Recording error';
        setError(message);
        cleanup();
      };

      // Start recording
      recorder.start();
      setIsRecording(true);
      startTimeRef.current = Date.now();

      // Track duration
      durationIntervalRef.current = window.setInterval(() => {
        const elapsed = (Date.now() - startTimeRef.current) / 1000;
        setDuration(Math.floor(elapsed));
      }, 100); // Update every 100ms for smooth UI

      // Auto-stop at max duration
      maxDurationTimeoutRef.current = window.setTimeout(() => {
        if (mediaRecorderRef.current?.state === 'recording') {
          stopRecording();
        }
      }, maxDuration * 1000);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to start recording';
      setError(message);
      cleanup();
      throw err;
    }
  }, [isSupported, mimeType, maxDuration, cleanup]);

  /**
   * Stop recording and return audio blob
   */
  const stopRecording = useCallback(async (): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const recorder = mediaRecorderRef.current;

      if (!recorder || recorder.state === 'inactive') {
        const error = new Error('No active recording');
        setError(error.message);
        reject(error);
        return;
      }

      // Handle stop event
      recorder.onstop = () => {
        const mimeType = recorder.mimeType || 'audio/webm';
        const audioBlob = new Blob(chunksRef.current, { type: mimeType });
        cleanup();
        resolve(audioBlob);
      };

      // Stop recording
      recorder.stop();
    });
  }, [cleanup]);

  /**
   * Cancel recording without returning blob
   */
  const cancelRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;

    if (recorder && recorder.state === 'recording') {
      recorder.stop();
    }

    cleanup();
  }, [cleanup]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current?.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
      cleanup();
    };
  }, [cleanup]);

  return {
    isSupported,
    isRecording,
    duration,
    audioLevel,
    error,
    startRecording,
    stopRecording,
    cancelRecording,
  };
}
